import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Typography, Button, IconButton, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Chip, TextField, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Tooltip, Collapse
} from '@material-ui/core'
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Block as BlockIcon, CheckCircle as CheckCircleIcon,
  ExpandMore, ExpandLess
} from '@material-ui/icons'

const COST_TYPES = [
  { value: 'labor', label: 'Labor', bg: '#e8f5e9', color: '#2e7d32' },
  { value: 'equipment', label: 'Equipment', bg: '#fff3e0', color: '#e65100' },
  { value: 'material', label: 'Materials', bg: '#e8eaf6', color: '#283593' },
  { value: 'subcontract', label: 'Subcontract', bg: '#e3f2fd', color: '#1565c0' },
  { value: 'disposal', label: 'Disposal', bg: '#fce4ec', color: '#c62828' }
]

const getCostTypeStyle = (costType) => COST_TYPES.find(c => c.value === costType) || {}

function computeTestPrice(components, qty, markupPct, laborRates, equipmentRates, productionRates, materials) {
  if (!qty || qty <= 0) return []
  const markup = 1 + (markupPct || 0) / 100

  // Compute lead labor hours first so equipment can reference it
  const leadLabor = components.find(c => c.costType === 'labor' && !c.followsLeadHours)
  let leadHours = 0
  if (leadLabor) {
    const pr = productionRates.find(r => String(r._id) === String(leadLabor.productionRateId)) || {}
    leadHours = pr.baseRate > 0 ? qty / pr.baseRate : 0
  }

  return components.map(comp => {
    let hours = 0
    let cost = 0

    if (comp.costType === 'labor') {
      const lr = laborRates.find(r => r.role === comp.laborRole) || {}
      const pr = productionRates.find(r => String(r._id) === String(comp.productionRateId)) || {}
      const rate = pr.baseRate || 0
      hours = comp.followsLeadHours ? leadHours : (rate > 0 ? qty / rate : 0)
      cost = hours * (lr.hourlyRate || 0)
    } else if (comp.costType === 'equipment') {
      const er = equipmentRates.find(r => String(r._id) === String(comp.equipmentRateId)) || {}
      // Equipment runs for the same duration as lead labor
      hours = leadHours
      cost = hours * (er.hourlyRate || 0)
    } else if (comp.costType === 'material') {
      const mat = materials.find(m => String(m._id) === String(comp.materialCatalogId)) || {}
      cost = (mat.bestPrice || 0) * qty
    } else {
      cost = (comp.defaultUnitCost || 0) * qty
    }

    return {
      description: comp.description || comp.costType,
      costType: comp.costType,
      hours: Math.round(hours * 100) / 100,
      cost: Math.round(cost * 100) / 100,
      sell: Math.round(cost * markup * 100) / 100,
      isOptional: comp.isOptional || false,
      hasRate: cost > 0
    }
  })
}

const ServiceTemplatesTable = ({ url }) => {
  const [services, setServices] = useState([])
  const [templates, setTemplates] = useState([])
  const [laborRates, setLaborRates] = useState([])
  const [equipmentRates, setEquipmentRates] = useState([])
  const [productionRates, setProductionRates] = useState([])
  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newService, setNewService] = useState({ name: '', category: '' })
  const [savingService, setSavingService] = useState(false)
  const [editDialog, setEditDialog] = useState(null)
  const [editComponents, setEditComponents] = useState([])
  const [saving, setSaving] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [testQty, setTestQty] = useState('')
  const [testMarkupPct, setTestMarkupPct] = useState(15)

  const fetchData = () => {
    setLoading(true)
    Promise.all([
      axios.get(`${url}/api/bid-estimation/services?includeInactive=true`),
      axios.get(`${url}/api/bid-estimation/service-templates`),
      axios.get(`${url}/api/bid-estimation/labor-rates`),
      axios.get(`${url}/api/bid-estimation/equipment-rates`),
      axios.get(`${url}/api/bid-estimation/production-rates`),
      axios.get(`${url}/api/bid-estimation/materials`)
    ]).then(([svcRes, tmplRes, lrRes, erRes, prRes, matRes]) => {
      setServices(svcRes.data || [])
      setTemplates(tmplRes.data || [])
      setLaborRates(lrRes.data || [])
      setEquipmentRates(erRes.data || [])
      setProductionRates(prRes.data || [])
      setMaterials(matRes.data || [])
      setLoading(false)
    }).catch(err => { console.error('Failed to fetch:', err); setLoading(false) })
  }

  useEffect(() => { fetchData() }, [url])

  const templateMap = useMemo(() => {
    const map = {}
    for (const t of templates) map[t.serviceId] = t
    return map
  }, [templates])

  const categories = useMemo(() => {
    const cats = [...new Set(services.map(s => s.category))].sort()
    return ['all', ...cats]
  }, [services])

  const filtered = categoryFilter === 'all' ? services : services.filter(s => s.category === categoryFilter)

  const handleAddService = () => {
    if (!newService.name.trim() || !newService.category.trim()) return
    setSavingService(true)
    const serviceId = newService.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    axios.post(`${url}/api/bid-estimation/services`, {
      serviceId, name: newService.name.trim(), category: newService.category.trim()
    }).then(() => {
      setSavingService(false); setAddDialogOpen(false)
      setNewService({ name: '', category: '' }); fetchData()
    }).catch(err => { setSavingService(false); console.error(err) })
  }

  const handleToggleActive = (service) => {
    axios.put(`${url}/api/bid-estimation/services/${service._id}`, { isActive: !service.isActive })
      .then(() => fetchData()).catch(err => console.error(err))
  }

  const handleDeleteService = (service) => {
    if (!window.confirm(`Delete "${service.name}"?`)) return
    axios.delete(`${url}/api/bid-estimation/services/${service._id}`)
      .then(() => fetchData()).catch(err => console.error(err))
  }

  const openEdit = (service) => {
    const tmpl = templateMap[service.serviceId]
    setEditComponents(tmpl ? JSON.parse(JSON.stringify(tmpl.components)) : [])
    setEditDialog({ service, template: tmpl })
    setTestOpen(false); setTestQty('')
  }

  const addComponent = () => {
    setEditComponents(prev => [...prev, { costType: 'labor', description: '', laborRole: '', isOptional: false, followsLeadHours: false }])
  }

  const updateComponent = (idx, field, value) => {
    setEditComponents(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c))
  }

  const removeComponent = (idx) => setEditComponents(prev => prev.filter((_, i) => i !== idx))

  const handleSaveRecipe = async () => {
    if (!editDialog) return
    setSaving(true)
    try {
      await axios.post(`${url}/api/bid-estimation/service-templates`, {
        serviceId: editDialog.service.serviceId,
        serviceName: editDialog.service.name,
        serviceCategory: editDialog.service.category,
        components: editComponents.filter(c => c.description || c.costType)
      })
      setSaving(false); setEditDialog(null); fetchData()
    } catch (e) { setSaving(false); console.error(e) }
  }

  const testResults = useMemo(() => {
    return computeTestPrice(editComponents, parseFloat(testQty) || 0, testMarkupPct,
      laborRates, equipmentRates, productionRates, materials)
  }, [editComponents, testQty, testMarkupPct, laborRates, equipmentRates, productionRates, materials])

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <FormControl size="small" variant="outlined" style={{ minWidth: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} label="Category">
            {categories.map(c => <MenuItem key={c} value={c}>{c === 'all' ? 'All Categories' : c}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
          {filtered.length} service{filtered.length !== 1 ? 's' : ''} · {templates.length} recipe{templates.length !== 1 ? 's' : ''}
        </Typography>
        <Button size="small" startIcon={<AddIcon />} variant="contained" color="primary"
          onClick={() => setAddDialogOpen(true)} style={{ textTransform: 'none' }}>Add Service</Button>
      </div>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Crew Recipe</TableCell>
              <TableCell align="center" style={{ width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(service => {
              const tmpl = templateMap[service.serviceId]
              const components = tmpl?.components || []
              const isSystem = !service.entity
              const isInactive = !service.isActive
              return (
                <TableRow key={service._id} hover style={{ opacity: isInactive ? 0.5 : 1 }}>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">{service.category}</Typography>
                    {isSystem && <Chip size="small" label="Sys" style={{ marginLeft: 4, height: 16, fontSize: 9 }} />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>{service.name}</Typography>
                    {isInactive && <Chip size="small" label="Inactive" style={{ marginLeft: 4, height: 16, fontSize: 9, backgroundColor: '#ffebee' }} />}
                  </TableCell>
                  <TableCell>
                    {components.length === 0 ? (
                      <Typography variant="caption" color="textSecondary">No recipe</Typography>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {components.map((comp, idx) => {
                          const ct = getCostTypeStyle(comp.costType)
                          return <Chip key={idx} size="small"
                            label={`${comp.description || comp.costType}${comp.isOptional ? ' (opt)' : ''}`}
                            style={{ backgroundColor: ct.bg, color: ct.color, fontSize: 10, margin: 1, height: 20 }} />
                        })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(service)}><EditIcon fontSize="small" /></IconButton>
                    <Tooltip title={isInactive ? 'Reactivate' : 'Deactivate'}>
                      <IconButton size="small" onClick={() => handleToggleActive(service)}>
                        {isInactive ? <CheckCircleIcon fontSize="small" style={{ color: '#4caf50' }} /> : <BlockIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {!isSystem && <IconButton size="small" onClick={() => handleDeleteService(service)}><DeleteIcon fontSize="small" /></IconButton>}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Service Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Service Name" variant="outlined"
            value={newService.name} onChange={e => setNewService(prev => ({ ...prev, name: e.target.value }))} />
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Category</InputLabel>
            <Select value={newService.category} onChange={e => setNewService(prev => ({ ...prev, category: e.target.value }))} label="Category">
              {categories.filter(c => c !== 'all').map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddService} variant="contained" color="primary"
            disabled={savingService || !newService.name.trim() || !newService.category.trim()}
            style={{ textTransform: 'none' }}>{savingService ? 'Adding...' : 'Add Service'}</Button>
        </DialogActions>
      </Dialog>

      {/* Recipe Editor Dialog */}
      <Dialog open={!!editDialog} onClose={() => !saving && setEditDialog(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Service Config — {editDialog?.service?.name || ''}
          <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
            Define the default crew composition. Components auto-populate when this service is assigned.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {editComponents.map((comp, idx) => (
            <Paper key={idx} variant="outlined" style={{ padding: 10, marginBottom: 6 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={2}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Type</InputLabel>
                    <Select value={comp.costType} onChange={e => updateComponent(idx, 'costType', e.target.value)} label="Type">
                      {COST_TYPES.map(ct => <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <TextField fullWidth size="small" variant="outlined" label="Description"
                    value={comp.description || ''} onChange={e => updateComponent(idx, 'description', e.target.value)} />
                </Grid>
                {comp.costType === 'labor' && (
                  <>
                    <Grid item xs={2}>
                      <FormControl fullWidth size="small" variant="outlined">
                        <InputLabel>Labor Role</InputLabel>
                        <Select value={comp.laborRole || ''} onChange={e => updateComponent(idx, 'laborRole', e.target.value)} label="Labor Role">
                          <MenuItem value="">Custom</MenuItem>
                          {laborRates.map(lr => <MenuItem key={lr._id} value={lr.role}>{lr.role} (${lr.hourlyRate}/hr)</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth size="small" variant="outlined">
                        <InputLabel>Prod Rate</InputLabel>
                        <Select value={comp.productionRateId || ''} onChange={e => updateComponent(idx, 'productionRateId', e.target.value)} label="Prod Rate">
                          <MenuItem value="">None</MenuItem>
                          {productionRates.map(pr => <MenuItem key={pr._id} value={pr._id}>{pr.description} ({pr.baseRate} {pr.unit})</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                {comp.costType === 'equipment' && (
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel>Equipment</InputLabel>
                      <Select value={comp.equipmentRateId || ''} onChange={e => updateComponent(idx, 'equipmentRateId', e.target.value)} label="Equipment">
                        <MenuItem value="">None</MenuItem>
                        {equipmentRates.map(er => <MenuItem key={er._id} value={er._id}>{er.name} (${er.hourlyRate}/hr)</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {comp.costType === 'material' && (
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel>Catalog Item</InputLabel>
                      <Select value={comp.materialCatalogId || ''} onChange={e => updateComponent(idx, 'materialCatalogId', e.target.value)} label="Catalog Item">
                        <MenuItem value="">User selects at pricing</MenuItem>
                        {materials.slice(0, 50).map(m => <MenuItem key={m._id} value={m._id}>{m.description} (${m.bestPrice || '?'})</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {(comp.costType === 'subcontract' || comp.costType === 'disposal') && (
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" variant="outlined" label="Default $/unit" type="number"
                      value={comp.defaultUnitCost || ''} onChange={e => updateComponent(idx, 'defaultUnitCost', parseFloat(e.target.value) || 0)} />
                  </Grid>
                )}
                <Grid item xs={1}>
                  <FormControlLabel control={<Checkbox checked={comp.isOptional || false} onChange={e => updateComponent(idx, 'isOptional', e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Opt</Typography>} />
                </Grid>
                {comp.costType === 'labor' && (
                  <Grid item xs={1}>
                    <FormControlLabel control={<Checkbox checked={comp.followsLeadHours || false} onChange={e => updateComponent(idx, 'followsLeadHours', e.target.checked)} size="small" />}
                      label={<Typography variant="caption">Follows</Typography>} />
                  </Grid>
                )}
                <Grid item xs={1}>
                  <IconButton size="small" onClick={() => removeComponent(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button startIcon={<AddIcon />} onClick={addComponent} style={{ textTransform: 'none', marginTop: 8 }}>Add Component</Button>

          {/* Test Price Preview */}
          <div style={{ marginTop: 16 }}>
            <Button size="small" onClick={() => setTestOpen(!testOpen)}
              endIcon={testOpen ? <ExpandLess /> : <ExpandMore />}
              style={{ textTransform: 'none', fontSize: 12 }}>Test Price Preview</Button>
            <Collapse in={testOpen}>
              <Paper variant="outlined" style={{ padding: 12, marginTop: 8 }}>
                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" variant="outlined" label="Test Quantity" type="number"
                      value={testQty} onChange={e => setTestQty(e.target.value)} placeholder="e.g. 100" />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" variant="outlined" label="Markup %" type="number"
                      value={testMarkupPct} onChange={e => setTestMarkupPct(parseFloat(e.target.value) || 0)} />
                  </Grid>
                </Grid>
                {testResults.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Component</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">Sell</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testResults.map((r, idx) => (
                        <TableRow key={idx} style={{
                          fontStyle: r.isOptional ? 'italic' : 'normal',
                          backgroundColor: !r.hasRate ? '#fff8e1' : 'transparent'
                        }}>
                          <TableCell style={{ fontSize: 12 }}>{r.description}{r.isOptional ? ' (opt)' : ''}</TableCell>
                          <TableCell><Chip size="small" label={r.costType}
                            style={{ backgroundColor: getCostTypeStyle(r.costType).bg, color: getCostTypeStyle(r.costType).color, fontSize: 9, height: 18 }} /></TableCell>
                          <TableCell align="right" style={{ fontSize: 12 }}>{r.hours || '—'}</TableCell>
                          <TableCell align="right" style={{ fontSize: 12 }}>${r.cost.toLocaleString()}</TableCell>
                          <TableCell align="right" style={{ fontSize: 12 }}>${r.sell.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} style={{ fontWeight: 700, fontSize: 12 }}>Total (non-optional)</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, fontSize: 12 }}>
                          ${testResults.filter(r => !r.isOptional).reduce((s, r) => s + r.cost, 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, fontSize: 12 }}>
                          ${testResults.filter(r => !r.isOptional).reduce((s, r) => s + r.sell, 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Collapse>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveRecipe} variant="contained" color="primary" disabled={saving}
            style={{ textTransform: 'none' }}>{saving ? 'Saving...' : 'Save Recipe'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ServiceTemplatesTable
